/**
 * TopBarActions Component
 * Shared action buttons: team, language selector, theme toggle, user profile menu
 * Used by both TopBar and FloatingNavBar
 */
import { memo, useCallback } from 'react';

import { useGeoMarkupStore } from '@/store';
import {
  selectIsAuthenticated,
  selectCurrentWorkspaceId,
} from '@/store/selectors/authSelectors';
import { TeamButton } from '@/components/workspace/TeamButton';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { ThemeToggleButton } from '@/components/ui/ThemeToggleButton';
import { UserProfileMenu } from '@/components/user';
import { useDeviceLayout } from '@/hooks';
import styles from './TopBar.module.css';

interface TopBarActionsProps {
  readonly className?: string;
  readonly variant?: 'full' | 'compact';
}

export const TopBarActions = memo(function TopBarActions({
  className,
  variant,
}: TopBarActionsProps) {
  const { isMobile } = useDeviceLayout();
  const isAuthenticated = useGeoMarkupStore(selectIsAuthenticated);
  const currentWorkspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);
  const openManagementPanel = useGeoMarkupStore(
    (state) => state.workspace.openManagementPanel
  );
  const onlineCount = useGeoMarkupStore(
    (state) => Object.keys(state.presence.userStatuses).length
  );
  const buttonVariant = variant ?? (isMobile ? 'compact' : 'full');

  const handleTeamClick = useCallback(() => {
    if (currentWorkspaceId) {
      openManagementPanel(currentWorkspaceId, 'members');
    }
  }, [currentWorkspaceId, openManagementPanel]);

  return (
    <div className={`${styles.topBarActions} ${className || ''}`}>
      {isAuthenticated && (
        <TeamButton
          onlineCount={onlineCount}
          variant={buttonVariant}
          onClick={handleTeamClick}
        />
      )}
      <LanguageSelector size="md" />
      <ThemeToggleButton />
      {isAuthenticated && <UserProfileMenu size="md" />}
    </div>
  );
});
